import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import useCategorizedPages from 'hooks/app/useCategorizedPages';
import PageOutlet from '../PageOutlet';
import HBox from 'components/common/HBox';
import VBox from 'components/common/VBox';
import SelectButton from 'components/common/SelectButton';
import FocusableButton from 'components/common/FocusableButton';
import Segment from 'components/common/Segment';
import DIRECTION_VERTICAL from 'constants/DIRECTION_VERTICAL';
import useSimulationMeal from 'hooks/communication/useSimulationMeal';
import React from 'react';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import * as Signals from 'modules/Signals';
import DIRECTION_HORIZONTAL from 'constants/DIRECTION_HORIZONTAL';
import useSimulationMealResult from 'hooks/communication/useSimulationMealResult';
import useMessageBox from 'hooks/app/useMessageBox';
import useDetectEffect from 'hooks/common/useDetectEffect';

const category = 'SimulationDiet';
export default function SimulationDiet(props) {
  const {
    renderAll,
    front,
    ...rest
  } = props;

  const {
    requestSimulationMeal,
  } = useSimulationMeal();

  const [show1, setOnly1] = Recoil.useRecoilState(AppStore.assets.show1);
  const [std_food_name, setStdFoodName] = Recoil.useRecoilState(AppStore.assets.std_food_name);
  const [cp_food_names, setCpFoodNames] = Recoil.useRecoilState(AppStore.assets.cp_food_names);
  const cp_food_name_hash = React.useMemo(() => cp_food_names.reduce((a, c, index) => ({ ...a, [index]: c }), {}), [cp_food_names]);

  const [data, setData] = Recoil.useRecoilState(AppStore.assets.sim_meal);
  const period_start = Recoil.useRecoilValue(AppStore.assets.period_start);
  const period_end = Recoil.useRecoilValue(AppStore.assets.period_end);
  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  React.useEffect(() => {
    if (!patient_no) return;
    setData(() => ({}));
    Signals.signal('diet_data', {});
    requestSimulationMeal({
      patient_no,
      search_start_time: period_start,
      search_end_time: period_end,
    }, (success, data) => {
      if (success) {
        setData(() => data);
        Signals.signal('diet_data', data);
      } else {
        setData(() => ({}));
        Signals.signal('diet_data', {});
      }
    })
  }, [patient_no, period_end, period_start, requestSimulationMeal, setData]);

  const {
    high_bldsugr_list = [],
    low_bldsugr_list = [],
  } = data || {};
  const [names, hash] = React.useMemo(() => {
    const list = [];
    const hash = {};
    high_bldsugr_list.forEach(item => {
      const {
        food_name,
        intake_cnt,
        bldsugr_rise_w,
      } = item;
      if (!show1 && intake_cnt < 2) return;
      // if (bldsugr_rise_w == null) return;
      list.push(food_name);
      hash[food_name] = bldsugr_rise_w;
    });
    // low_bldsugr_list.forEach(item => {
    //   const {
    //     food_name,
    //     intake_cnt,
    //     bldsugr_rise_w,
    //   } = item;
    //   if (!show1 && intake_cnt < 2) return;
    //   if (!list.includes(food_name)) list.push(food_name);
    //   hash[food_name] = bldsugr_rise_w;
    // });
    return [list, hash];
  }, [high_bldsugr_list, show1]);

  const [result, setResult] = React.useState({ uninitialized: true });

  const onChangeStdFoodName = React.useCallback((e) => {
    setResult(() => ({ uninitialized: true }));
    setStdFoodName(() => e.target.value);
  }, [setStdFoodName]);

  const onChangeCpFoodName = React.useCallback((index) => (e) => {
    setResult(() => ({ uninitialized: true }));
    setCpFoodNames(([...ns]) => {
      ns[index] = e.target.value;
      return ns;
    });
  }, [setCpFoodNames]);

  // 혈당 차트를 위한 최대/최소값
  const [min, max] = React.useMemo(() => {
    let min = (high_bldsugr_list[0] || {}).bldsugr_rise_w || 50;
    let max = (high_bldsugr_list[0] || {}).bldsugr_rise_w || 250;
    high_bldsugr_list.forEach((item) => {
      const {
        bldsugr_rise_w,
      } = item;
      if (bldsugr_rise_w < min) min = bldsugr_rise_w;
      if (max < bldsugr_rise_w) max = bldsugr_rise_w;
    });
    low_bldsugr_list.forEach((item) => {
      const {
        bldsugr_rise_w,
      } = item;
      if (bldsugr_rise_w < min) min = bldsugr_rise_w;
      if (max < bldsugr_rise_w) max = bldsugr_rise_w;
    });
    return [Math.floor(min / 50) * 50, Math.ceil(max / 50) * 50 + 50];
  }, [high_bldsugr_list, low_bldsugr_list]);


  const {
    requestSimulationMealResult,
  } = useSimulationMealResult();
  const onExecute = React.useCallback(() => {
    if (!patient_no) return;
    setResult(() => ({}));
    requestSimulationMealResult({
      patient_no,
      search_start_time: period_start,
      search_end_time: period_end,
      std_food_name,
      cp_food_name_list: Object.keys(cp_food_name_hash).filter(k => !!cp_food_name_hash[k]).map(k => ({ food_name: cp_food_name_hash[k] })),
    }, (success, data) => {
      if (success) {
        setResult(() => data);
      } else {
        setResult(() => ({}));
      }
    })
  }, [cp_food_name_hash, patient_no, period_end, period_start, requestSimulationMealResult, std_food_name]);

  const canvas_list = React.useMemo(() => {
    const list = [];
    if (!std_food_name) return list;
    const check = {};
    list.push(std_food_name);
    check[std_food_name] = true;
    list.push(...Object.keys(cp_food_name_hash).filter(k => !!cp_food_name_hash[k] && !check[cp_food_name_hash[k]]).map(k => cp_food_name_hash[k]));
    return list;
  }, [cp_food_name_hash, std_food_name]);

  // 차트 정보, 탄단지 차트를 위한 최대 최소값
  const [chart0, chart1, min_amt, max_amt] = React.useMemo(() => {

    const {
      std_food_avg_bldsugr_rise_w,
      std_food_min_bldsugr_rise_w,
      std_food_max_bldsugr_rise_w,
      cp_food_list = [],
      // .cp_food_name,
      // .cp_food_min_bldsugr_rise_w,
      // .cp_food_max_bldsugr_rise_w,
      std_food_carbo_amt,
      std_food_protein_amt,
      std_food_fat_amt,
      std_food_intake_amt,
      cp_food_cpf_list = [],
      // .cp_food_name,
      // .cp_food_carbo_amt,
      // .cp_food_protein_amt,
      // .cp_food_fat_amt,
      // .cp_food_intake_amt,
    } = result || {};

    const chart0 = {};
    const chart1 = [];
    chart0[0] = {
      avg: std_food_avg_bldsugr_rise_w,
      min: std_food_min_bldsugr_rise_w,
      max: std_food_max_bldsugr_rise_w
    };
    cp_food_list.forEach(item => {
      const {
        cp_food_name,
        cp_food_avg_bldsugr_rise_w,
        cp_food_min_bldsugr_rise_w,
        cp_food_max_bldsugr_rise_w,
      } = item;
      const index = Object.values(cp_food_name_hash).findIndex(v => v === cp_food_name);
      chart0[index + 1] = {
        avg: cp_food_avg_bldsugr_rise_w,
        min: cp_food_min_bldsugr_rise_w,
        max: cp_food_max_bldsugr_rise_w,
      };
    });

    chart1[0] = [
      std_food_intake_amt,
      std_food_fat_amt + std_food_protein_amt + std_food_carbo_amt,
      std_food_fat_amt,
      std_food_protein_amt,
      std_food_carbo_amt
    ];

    cp_food_cpf_list.forEach(item => {
      const {
        cp_food_cpf_name,
        cp_food_carbo_amt,
        cp_food_protein_amt,
        cp_food_fat_amt,
        cp_food_intake_amt,
      } = item;
      const index = Object.values(cp_food_name_hash).findIndex(v => v === cp_food_cpf_name);
      chart1[index + 1] = [
        cp_food_intake_amt,
        cp_food_fat_amt + cp_food_protein_amt + cp_food_carbo_amt,
        cp_food_fat_amt,
        cp_food_protein_amt,
        cp_food_carbo_amt
      ];
    });

    const length0 = Object.keys(chart0).length;
    const length1 = Object.keys(chart1).length;
    const list0 = [];
    const list1 = [];
    for (let i = 0; i < length0; ++i) {
      list0.push(chart0[i] || {});
    }
    for (let i = 0; i < length1; ++i) {
      list1.push(chart1[i] || []);
    }

    const min_amt = Math.floor(Math.min(...list1.map(a => a[0])) / 50) * 50;
    const max_amt = Math.ceil(Math.max(...list1.map(a => a[0])) / 50) * 50;
    return [list0, list1, min_amt, max_amt * 1.5];
  }, [cp_food_name_hash, result]);

  const {
    open,
    update,
    close,
  } = useMessageBox('App');

  React.useEffect(() => {
    if (std_food_name !== '' && hash[std_food_name] === null) {
      open({
        id: 'simulation',
        title: T('음식 선택'),
        contents: (
          <VBox>
            <div>
              {T('혈당 데이터가 없는 음식입니다.')}
            </div>
          </VBox>
        ),
        buttons: [ex('primary', {
          autoFocus: true,
          caption: T('확인'),
          event: 'onClose',
        })],
        events: {
          onClose: () => {
            close('simulation');
            setStdFoodName(() => '');
          },
        },
      });
    }
    if (cp_food_names.filter((name, index) => name !== '' && hash[name] === null).length) {
      open({
        id: 'simulation',
        title: T('음식 선택'),
        contents: (
          <VBox>
            <div>
              {T('혈당 데이터가 없는 음식입니다.')}
            </div>
          </VBox>
        ),
        buttons: [ex('primary', {
          autoFocus: true,
          caption: T('확인'),
          event: 'onClose',
        })],
        events: {
          onClose: () => {
            close('simulation');
            setCpFoodNames(([...ns]) => {
              ns.forEach((n, index) => {
                if (hash[n] == null) {
                  ns[index] = '';
                }
              });
              return ns;
            });
          },
        },
      });
    }
  }, [close, cp_food_name_hash, cp_food_names, hash, open, setCpFoodNames, setStdFoodName, std_food_name]);

  const pages = useCategorizedPages(category);
  return (
    <VBox {...ex('SimulationDiet', rest)}>
      <HBox>
        <Segment full>
          <PageOutlet
            {...{
              renderAll,
              category,
              pages,
              front,
            }}
          />
        </Segment>
        <Segment {...{ direction: DIRECTION_VERTICAL }}>
          <VBox {...ex('StepBox')}>
            <HBox vcenter {...ex('StepTitle')}>
              <div>{T('STEP 1')}</div>
              <div>{T('왼쪽에서 기준 음식을 선택해 주세요.')}</div>
            </HBox>
            <HBox vcenter {...ex('StepSelection')}>
              <SelectButton
                {...ex({ selected: !!std_food_name })}
                {...{
                  placeholder: T('음식명'),
                  value: std_food_name || '',
                  options: [
                    { label: T('음식명'), value: '' },
                    ...names.map(name => ({
                      value: name,
                      label: name,
                    }))
                  ],
                  onChange: onChangeStdFoodName,
                }}
              />
              <HBox vcenter end>
                <div>
                  {hash[std_food_name] ?? '-'}
                </div>
                <div>
                  {T('mg/dL')}
                </div>
              </HBox>
            </HBox>
          </VBox>
          <VBox {...ex('StepBox')}>
            <div {...ex('extra')}>{T('* 최대 3개 선택 가능')}</div>
            <HBox vcenter {...ex('StepTitle')}>
              <div>{T('STEP 2')}</div>
              <div>{T('왼쪽에서 비교 음식을 선택해 주세요.')}</div>
            </HBox>
            <HBox vcenter {...ex('StepSelection')}>
              <SelectButton
                {...ex({ selected: !!cp_food_name_hash[0] })}
                {...{
                  placeholder: T('음식명'),
                  value: cp_food_name_hash[0] || '',
                  options: [
                    { label: T('음식명'), value: '' },
                    ...names.map(name => ({
                      value: name,
                      label: name,
                    }))
                  ],
                  onChange: onChangeCpFoodName(0),
                }}
              />
              <HBox vcenter end>
                <div>
                  {hash[cp_food_name_hash[0]] || '-'}
                </div>
                <div>
                  {T('mg/dL')}
                </div>
              </HBox>
            </HBox>
            <HBox vcenter {...ex('StepSelection')}>
              <SelectButton
                {...ex({ selected: !!cp_food_name_hash[1] })}
                {...{
                  placeholder: T('음식명'),
                  value: cp_food_name_hash[1] || '',
                  options: [
                    { label: T('음식명'), value: '' },
                    ...names.map(name => ({
                      value: name,
                      label: name,
                    }))
                  ],
                  onChange: onChangeCpFoodName(1),
                }}
              />
              <HBox vcenter end>
                <div>
                  {hash[cp_food_name_hash[1]] || '-'}
                </div>
                <div>
                  {T('mg/dL')}
                </div>
              </HBox>
            </HBox>
            <HBox vcenter {...ex('StepSelection')}>
              <SelectButton
                {...ex({ selected: !!cp_food_name_hash[2] })}
                {...{
                  placeholder: T('음식명'),
                  value: cp_food_name_hash[2] || '',
                  options: [
                    { label: T('음식명'), value: '' },
                    ...names.map(name => ({
                      value: name,
                      label: name,
                    }))
                  ],
                  onChange: onChangeCpFoodName(2),
                }}
              />
              <HBox vcenter end>
                <div>
                  {hash[cp_food_name_hash[2]] || '-'}
                </div>
                <div>
                  {T('mg/dL')}
                </div>
              </HBox>
            </HBox>
          </VBox>
          <VBox {...ex('StepBox')}>
            <HBox vcenter {...ex('StepTitle')}>
              <div>{T('STEP 3')}</div>
              <div>{T('실행하기 버튼을 눌러 결과를 확인하세요.')}</div>
            </HBox>
            <HBox center>
              <FocusableButton
                {...ex(['ExecuteButton', 'primary'])}
                {...{
                  onClick: onExecute,
                }}
              >
                {T('실행하기')}
              </FocusableButton>
            </HBox>
          </VBox>
        </Segment>
      </HBox>
      <HBox {...ex('SimulationDietResult')}>
        {result?.uninitialized && (
          <div {...ex('Dim')} >
            <div>
              {T('실행하기 버튼을 누르면 결과를 볼 수 있어요!')}
            </div>
          </div>
        )}
        <Segment full {...{ direction: DIRECTION_HORIZONTAL }}>
          <div>
            {T('시뮬레이션 결과')}
          </div>

          <VBox full>
            <div>
              <div>
                {T('혈당상승폭 비교')}
              </div>
            </div>
            <div {...ex(['SimulationDietResultChart', 'Chart0'])}>
              <table>
                <tbody>
                  <tr>
                    <td>
                      <div {...ex('Y')}>
                        <div>{max}</div>
                        <div>{+((max * 2 + min) / 3).toFixed(0)}</div>
                        <div>{+((max + min) / 2).toFixed(0)}</div>
                        <div>{+((max + min * 2) / 3).toFixed(0)}</div>
                        <div>{min}</div>
                      </div>
                    </td>
                    <td>
                      <HBox {...ex('canvas')}>
                        {canvas_list.map((name, index) => (
                          <HBox center vcenter full key={index}>
                            <div
                              {...ex('bar')}
                              {...{
                                style: {
                                  bottom: `${(((chart0[index] || {}).min || 0) - min) / (max - min) * 100}%`,
                                  height: `${(((chart0[index] || {}).max || 0) - min) / (max - min) * 100 - (((chart0[index] || {}).min || 0) - min) / (max - min) * 100}%`,
                                }
                              }}
                            >
                              {(chart0[index] || {}).min === (chart0[index] || {}).min && (
                                <div {...ex(['info', 'same'])}>
                                  <div>
                                    {(chart0[index] || {}).max || 0}
                                  </div>
                                </div>
                              )}
                              {(chart0[index] || {}).min !== (chart0[index] || {}).min && (
                                <div {...ex('info')}>
                                  <div>
                                    {(chart0[index] || {}).max || 0}
                                  </div>
                                </div>
                              )}
                              {(chart0[index] || {}).min !== (chart0[index] || {}).min && (
                                <div {...ex('info')}>
                                  <div>
                                    {(chart0[index] || {}).min || 0}
                                  </div>
                                </div>
                              )}

                              <div {...ex(['balloon', { plus: 0 <= Math.sign((chart0[index] || {}).avg - (chart0[0] || {}).avg) }])}>
                                {((chart0[index] || {}).avg || 0) - ((chart0[0] || {}).avg || 0)}
                              </div>

                            </div>
                            <div
                              {...ex('avg')}
                              {...{
                                style: {
                                  bottom: `${(((chart0[index] || {}).avg || 0) - min) / (max - min) * 100}%`,
                                }
                              }}
                            >
                              <div {...ex('info')}>
                                <div>
                                  {(chart0[index] || {}).avg || 0}
                                </div>
                              </div>
                            </div>
                          </HBox>
                        ))}
                      </HBox>
                    </td>
                  </tr>
                  <tr>
                    <td>

                    </td>
                    <td>
                      <HBox {...ex('X')}>
                        {canvas_list.map(name => (
                          <HBox center vcenter full key={name}>
                            <div>{name}</div>
                          </HBox>
                        ))}
                      </HBox>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </VBox>
          <VBox full>
            <HBox between vcenter>
              <div>
                {T('탄단지 비율 비교')}
              </div>

              <HBox vcenter>
                <HBox vcenter {...ex('SimulationDietResultLegend')}>
                  <div />
                  <div>
                    {T('탄수화물')}
                  </div>
                </HBox>
                <HBox vcenter {...ex('SimulationDietResultLegend')}>
                  <div />
                  <div>
                    {T('단백질')}
                  </div>
                </HBox>
                <HBox vcenter {...ex('SimulationDietResultLegend')}>
                  <div />
                  <div>
                    {T('지방')}
                  </div>
                </HBox>
              </HBox>
            </HBox>
            <div {...ex(['SimulationDietResultChart', 'Chart1'])}>
              <table>
                <tbody>
                  <tr>
                    <td>
                      <div {...ex('Y')}>

                      </div>
                    </td>
                    <td>
                      <HBox {...ex('canvas')}>
                        {canvas_list.map((name, index) => (
                          <HBox center vcenter full key={index}>
                            <VBox
                              {...ex('mbar')}
                              {...{
                                style: {
                                  height: `${(((chart1[index] || [])[0] || 0) - min_amt) / (max_amt - min_amt) * 100 - (((chart0[index] || [])[0] || 0) - min_amt) / (max_amt - min_amt) * 100}%`,
                                }
                              }}
                            >
                              <div {...{ style: { height: `${(chart1[index] || [])[2] / (chart1[index] || [])[1] * 100}%` } }} />
                              <div {...{ style: { height: `${(chart1[index] || [])[3] / (chart1[index] || [])[1] * 100}%` } }} />
                              <div {...{ style: { height: `${(chart1[index] || [])[4] / (chart1[index] || [])[1] * 100}%` } }} />

                              <div>
                                {T('{0} - {1} - {2}',
                                  (chart1[index] || [])[2],
                                  (chart1[index] || [])[3],
                                  (chart1[index] || [])[4]
                                )}
                              </div>
                            </VBox>
                          </HBox>
                        ))}
                      </HBox>
                    </td>
                  </tr>
                  <tr>
                    <td>

                    </td>
                    <td>
                      <HBox {...ex('X')}>
                        {canvas_list.map(name => (
                          <HBox center vcenter full key={name}>
                            <div>{name}</div>
                          </HBox>
                        ))}
                      </HBox>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </VBox>
        </Segment>

      </HBox>
    </VBox >
  );
}
